import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["id", "value", "children", "renderBeforeLabel", "renderAfterLabel"],
  _excluded2 = ["id", "renderLabel", "children"],
  _excluded3 = ["renderLabel", "value", "defaultValue", "id", "size", "assistiveText", "placeholder", "interaction", "isRequired", "isInline", "width", "optionsMaxWidth", "optionsMaxHeight", "visibleOptionsCount", "messages", "placement", "constrain", "mountNode", "inputRef", "listRef", "renderEmptyOption", "renderBeforeInput", "renderAfterInput", "onFocus", "onBlur", "onShowOptions", "onHideOptions", "children", "layout"];
var _dec, _class, _SimpleSelect;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { isValidElement, Component, Children } from 'react';
import * as utils from '@instructure/ui-utils';
import { matchComponentTypes, passthroughProps, callRenderProp, getInteraction, withDeterministicId } from '@instructure/ui-react-utils';
import { Select } from '@instructure/ui-select';
import { Option } from "./Option/index.js";
import { Group } from "./Group/index.js";
import { allowedProps } from "./props.js";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
---
category: components
tags: form, field, dropdown
---
**/
let SimpleSelect = (_dec = withDeterministicId(), _dec(_class = (_SimpleSelect = class SimpleSelect extends Component {
  constructor(props) {
    super(props);
    this.ref = null;
    this._emptyOptionId = void 0;
    this.getOption = (field, value) => {
      const children = Children.toArray(this.props.children);
      let match;
      for (let i = 0; i < children.length; ++i) {
        const child = children[i];
        if (matchComponentTypes(child, [Option])) {
          if (child.props[field] === value) {
            match = child;
          }
        } else if (matchComponentTypes(child, [Group])) {
          const groupChildren = Children.toArray(child.props.children);
          for (let j = 0; j < groupChildren.length; ++j) {
            const groupChild = groupChildren[j];
            if (groupChild.props[field] === value) {
              match = groupChild;
              break;
            }
          }
        }
        if (match) break;
      }
      return match;
    };
    this.handleRef = node => {
      this.ref = node;
    };
    this.handleBlur = event => {
      this.setState({
        highlightedOptionId: void 0
      });
      if (typeof this.props.onBlur === 'function') {
        this.props.onBlur(event);
      }
    };
    this.handleShowOptions = event => {
      this.setState({
        isShowingOptions: true
      });
      if (typeof this.props.onShowOptions === 'function') {
        this.props.onShowOptions(event);
      }
      if (event.type.startsWith('key')) {
        const keyboardEvent = event;
        const children = Children.toArray(this.props.children);
        if (!this.state.inputValue && children.length > 0) {
          const position = keyboardEvent.key === 'ArrowDown' ? 'first' : keyboardEvent.key === 'ArrowUp' ? 'last' : void 0;
          if (position) {
            var _this$getOptionByPosi;
            const optionId = (_this$getOptionByPosi = this.getOptionByPosition(position)) === null || _this$getOptionByPosi === void 0 ? void 0 : _this$getOptionByPosi.props.id;
            optionId && this.setState({
              highlightedOptionId: optionId
            });
          }
        }
      }
    };
    this.handleHideOptions = event => {
      this.setState(state => {
        const option = this.getOption('id', state.selectedOptionId);
        return {
          isShowingOptions: false,
          highlightedOptionId: void 0,
          inputValue: option ? option.props.children : ''
        };
      });
      if (typeof this.props.onHideOptions === 'function') {
        this.props.onHideOptions(event);
      }
    };
    this.handleHighlightOption = (_event, {
      id
    }) => {
      if (id === this._emptyOptionId) return;
      this.setState({
        highlightedOptionId: id,
        inputValue: this.state.inputValue
      });
    };
    this.handleSelectOption = (event, {
      id
    }) => {
      if (id === this._emptyOptionId) {
        // selected option is the empty option
        this.setState({
          isShowingOptions: false
        });
        return;
      }
      const option = this.getOption('id', id);
      const value = option && option.props.value;

      // Focus needs to be reapplied to input
      // after selecting an item to make sure VoiceOver behaves correctly on iOS
      if (utils.isAndroidOrIOS()) {
        this.blur();
        this.focus();
      }
      if (this.isControlled) {
        this.setState({
          isShowingOptions: false
        });
      } else {
        this.setState(state => ({
          isShowingOptions: false,
          selectedOptionId: id,
          inputValue: option ? option.props.children : state.inputValue
        }));
      }
      // fire onChange if selected option changed
      if (option && typeof this.props.onChange === 'function') {
        this.props.onChange(event, {
          value,
          id
        });
      }
      // hide options list whenever selection is made
      if (typeof this.props.onHideOptions === 'function') {
        this.props.onHideOptions(event);
      }
    };
    const _option = this.getInitialOption(props);
    this.state = {
      inputValue: _option ? _option.props.children : '',
      isShowingOptions: false,
      highlightedOptionId: void 0,
      selectedOptionId: _option ? _option.props.id : void 0
    };
    this._emptyOptionId = props.deterministicId('Select-EmptyOption');
  }
  get _select() {
    return this.ref;
  }
  focus() {
    this.ref && this.ref.focus();
  }
  blur() {
    this.ref && this.ref.blur();
  }
  get focused() {
    return this.ref ? this.ref.focused : false;
  }
  get id() {
    return this.ref ? this.ref.id : void 0;
  }
  get isControlled() {
    return typeof this.props.value !== 'undefined';
  }
  get interaction() {
    return getInteraction({
      props: this.props
    });
  }
  hasOptionsChanged(prevChildren, currentChildren) {
    const getValues = children => Children.map(children, child => {
      if (/*#__PURE__*/isValidElement(child)) {
        return child.props.value;
      }
      return null;
    });
    const prevValues = getValues(prevChildren);
    const currentValues = getValues(currentChildren);
    return JSON.stringify(prevValues) !== JSON.stringify(currentValues);
  }
  componentDidUpdate(prevProps) {
    if (this.hasOptionsChanged(prevProps.children, this.props.children)) {
      // Compare current input value to children's child prop, this is put into
      // state.inputValue
      const option = this.getOption('children', this.state.inputValue);
      this.setState({
        inputValue: option ? option.props.children : void 0,
        selectedOptionId: option ? option.props.id : ''
      });
    }
    if (this.props.value !== prevProps.value) {
      // if value has changed externally try to find an option with the same value
      // and select it
      let option = this.getOption('value', this.props.value);
      if (typeof this.props.value === 'undefined') {
        // preserve current value when changing from controlled to uncontrolled
        option = this.getOption('value', prevProps.value);
      }
      this.setState({
        inputValue: option ? option.props.children : '',
        selectedOptionId: option ? option.props.id : ''
      });
    }
  }
  getInitialOption(props) {
    const value = props.value,
      defaultValue = props.defaultValue;
    const initialValue = value || defaultValue;
    if (typeof initialValue === 'string' || typeof initialValue === 'number') {
      // get option based on value or defaultValue, if provided
      return this.getOption('value', initialValue);
    }
    // otherwise get the first option
    return this.getFirstOption();
  }
  getOptionLabelById(id) {
    const option = this.getOption('id', id);
    return option ? option.props.children : '';
  }
  getFirstOption() {
    const children = Children.toArray(this.props.children);
    let match;
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (matchComponentTypes(child, [Option])) {
        match = child;
      } else if (matchComponentTypes(child, [Group])) {
        // first child is a group, not an option, find first child in group
        match = Children.toArray(child.props.children)[0];
      }
      if (match) {
        break;
      }
    }
    return match;
  }
  getOptionByPosition(position) {
    const children = Children.toArray(this.props.children);

    // Determine where to start looking based on position
    const index = position === 'first' ? 0 : children.length - 1;

    // Check if child is an option or group
    const child = children[index];
    if (!child) return void 0;

    // If it's a regular option, return it
    if (matchComponentTypes(child, [Option])) {
      return child;
    }

    // If it's a group, get its options
    if (matchComponentTypes(child, [Group])) {
      const groupOptions = Children.toArray(child.props.children);
      const groupIndex = position === 'first' ? 0 : groupOptions.length - 1;
      return groupOptions[groupIndex];
    }
    return void 0;
  }
  renderChildren() {
    let children = Children.toArray(this.props.children);
    children = Children.map(children, child => {
      if (matchComponentTypes(child, [Option])) {
        return this.renderOption(child);
      } else if (matchComponentTypes(child, [Group])) {
        return this.renderGroup(child);
      }
      return null;
    }).filter(child => !!child);
    if (children.length === 0) {
      // no valid children, render empty option
      return this.renderEmptyOption();
    }
    return children;
  }
  renderEmptyOption() {
    return _jsx(Select.Option, {
      id: this._emptyOptionId,
      isHighlighted: false,
      isSelected: false,
      children: callRenderProp(this.props.renderEmptyOption)
    });
  }
  renderOption(option) {
    var _option$props$isDisab;
    const _option$props = option.props,
      id = _option$props.id,
      value = _option$props.value,
      children = _option$props.children,
      renderBeforeLabel = _option$props.renderBeforeLabel,
      renderAfterLabel = _option$props.renderAfterLabel,
      rest = _objectWithoutProperties(_option$props, _excluded);
    const isDisabled = (_option$props$isDisab = option.props.isDisabled) !== null && _option$props$isDisab !== void 0 ? _option$props$isDisab : false; // after the react 19 upgrade `isDisabled` is undefined instead of defaulting to false if not specified (but only in vitest env for some reason)
    const isSelected = id === this.state.selectedOptionId;
    const isHighlighted = id === this.state.highlightedOptionId;
    const getRenderLabel = renderLabel => {
      var _renderLabel$prototyp;
      if (typeof renderLabel === 'function' && !(renderLabel !== null && renderLabel !== void 0 && (_renderLabel$prototyp = renderLabel.prototype) !== null && _renderLabel$prototyp !== void 0 && _renderLabel$prototyp.isReactComponent)) {
        return renderLabel.bind(null, {
          id,
          isDisabled,
          isSelected,
          isHighlighted,
          children
        });
      }
      return renderLabel;
    };
    return _jsx(Select.Option, {
      id: id,
      value: value,
      isHighlighted: isHighlighted,
      isSelected: isSelected,
      isDisabled: isDisabled,
      renderBeforeLabel: getRenderLabel(renderBeforeLabel),
      renderAfterLabel: getRenderLabel(renderAfterLabel),
      ...passthroughProps(rest),
      children: children
    }, option.key || id);
  }
  renderGroup(group) {
    const _group$props = group.props,
      id = _group$props.id,
      renderLabel = _group$props.renderLabel,
      children = _group$props.children,
      rest = _objectWithoutProperties(_group$props, _excluded2);
    return _jsx(Select.Group, {
      renderLabel: renderLabel,
      ...passthroughProps(rest),
      children: Children.map(children, child => this.renderOption(child))
    }, group.key || id);
  }
  render() {
    const _this$props = this.props,
      renderLabel = _this$props.renderLabel,
      value = _this$props.value,
      defaultValue = _this$props.defaultValue,
      id = _this$props.id,
      size = _this$props.size,
      assistiveText = _this$props.assistiveText,
      placeholder = _this$props.placeholder,
      interaction = _this$props.interaction,
      isRequired = _this$props.isRequired,
      isInline = _this$props.isInline,
      width = _this$props.width,
      optionsMaxWidth = _this$props.optionsMaxWidth,
      optionsMaxHeight = _this$props.optionsMaxHeight,
      visibleOptionsCount = _this$props.visibleOptionsCount,
      messages = _this$props.messages,
      placement = _this$props.placement,
      constrain = _this$props.constrain,
      mountNode = _this$props.mountNode,
      inputRef = _this$props.inputRef,
      listRef = _this$props.listRef,
      renderEmptyOption = _this$props.renderEmptyOption,
      renderBeforeInput = _this$props.renderBeforeInput,
      renderAfterInput = _this$props.renderAfterInput,
      onFocus = _this$props.onFocus,
      onBlur = _this$props.onBlur,
      onShowOptions = _this$props.onShowOptions,
      onHideOptions = _this$props.onHideOptions,
      children = _this$props.children,
      layout = _this$props.layout,
      rest = _objectWithoutProperties(_this$props, _excluded3);
    return _jsx(Select, {
      renderLabel: renderLabel,
      inputValue: this.state.inputValue,
      isShowingOptions: this.state.isShowingOptions,
      id: id,
      size: size,
      assistiveText: assistiveText,
      placeholder: placeholder,
      interaction: this.interaction,
      isRequired: isRequired,
      isInline: isInline,
      width: width,
      optionsMaxWidth: optionsMaxWidth,
      optionsMaxHeight: optionsMaxHeight,
      visibleOptionsCount: visibleOptionsCount,
      messages: messages,
      placement: placement,
      constrain: constrain,
      mountNode: mountNode,
      ref: this.handleRef,
      inputRef: inputRef,
      listRef: listRef,
      renderBeforeInput: renderBeforeInput,
      renderAfterInput: renderAfterInput,
      onFocus: onFocus,
      onBlur: this.handleBlur,
      onRequestShowOptions: this.handleShowOptions,
      onRequestHideOptions: this.handleHideOptions,
      onRequestHighlightOption: this.handleHighlightOption,
      onRequestSelectOption: this.handleSelectOption,
      isOptionContentAppliedToInput: this.props.isOptionContentAppliedToInput,
      layout: layout,
      ...passthroughProps(rest),
      "data-cid": "SimpleSelect",
      children: this.renderChildren()
    });
  }
}, _SimpleSelect.displayName = "SimpleSelect", _SimpleSelect.componentId = 'SimpleSelect', _SimpleSelect.Option = Option, _SimpleSelect.Group = Group, _SimpleSelect.allowedProps = allowedProps, _SimpleSelect.defaultProps = {
  size: 'medium',
  isRequired: false,
  isInline: false,
  visibleOptionsCount: 8,
  placement: 'bottom stretch',
  constrain: 'window',
  renderEmptyOption: '---',
  isOptionContentAppliedToInput: false
}, _SimpleSelect)) || _class);
export { SimpleSelect };
export default SimpleSelect;